// Single source of truth for phone → wa.me conversion. Previously reimplemented separately
// in Subscriptions.jsx, Students.jsx, and TrialStudents.jsx (each with slightly different
// edge-case handling) — all three now import from here instead.
//
// Nepal mobile numbers are stored bare (no country code) in most of the app, e.g. "9841234567"
// or sometimes "09841234567" / "+9779841234567". WhatsApp's wa.me links need the full E.164
// digit string with country code, no '+', no spaces: "9779841234567".

/**
 * Converts a stored phone value to a WhatsApp-ready E.164 digit string (country code + number,
 * no '+'). Returns null when the value can't be confidently converted — callers should hide
 * the WhatsApp action entirely in that case rather than open a broken wa.me link.
 * @param {string} raw
 * @returns {string|null}
 */
export function toWhatsAppPhone(raw) {
  if (!raw) return null;
  const cleaned = String(raw).replace(/[\s\-().]/g, '');
  const digits  = (cleaned.startsWith('+') ? cleaned.slice(1) : cleaned).replace(/^00/, '');

  if (/^977[9]\d{9}$/.test(digits)) return digits;               // already +977 9XXXXXXXXX
  if (/^0[9]\d{9}$/.test(digits))   return '977' + digits.slice(1); // 0 9XXXXXXXXX (leading trunk 0)
  if (/^[9]\d{9}$/.test(digits))    return '977' + digits;        // bare 9XXXXXXXXX
  return null; // not a recognizable Nepali mobile number — don't guess
}

/**
 * Opens WhatsApp with a pre-filled message. Returns false (and opens nothing) when the phone
 * can't be converted — callers should check toWhatsAppPhone(phone) up front to decide whether
 * to show the action at all, rather than relying on this return value at click time.
 * @param {string} phone
 * @param {string} message
 * @returns {boolean}
 */
export function openWhatsApp(phone, message) {
  const waPhone = toWhatsAppPhone(phone);
  if (!waPhone) return false;
  window.open(`https://wa.me/${waPhone}?text=${encodeURIComponent(message)}`, '_blank', 'noopener,noreferrer');
  return true;
}
